import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';

export const CountdownButton = ({
  children,
  onConfirm,
  countdownSeconds = 20,
  variant = 'default',
  size = 'default',
  disabled = false,
  className = '',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  autoStart = false
}) => {
  const [isCountingDown, setIsCountingDown] = useState(autoStart);
  const [countdown, setCountdown] = useState(countdownSeconds);
  const [isLoading, setIsLoading] = useState(false);

  // Countdown effect
  useEffect(() => {
    let interval;

    if (isCountingDown && countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (isCountingDown && countdown === 0) {
      // Countdown completed - execute the action
      setIsCountingDown(false);
      setIsLoading(true);
      onConfirm().finally(() => {
        setIsLoading(false);
        setCountdown(countdownSeconds);
      });
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isCountingDown, countdown, onConfirm, countdownSeconds]);

  const handleClick = useCallback(() => {
    if (isLoading) return;

    if (isCountingDown) {
      // Cancel countdown
      setIsCountingDown(false);
      setCountdown(countdownSeconds);
    } else {
      // Start countdown
      setIsCountingDown(true);
      setCountdown(countdownSeconds);
    }
  }, [isCountingDown, isLoading, countdownSeconds]);

  const getButtonText = () => {
    if (isLoading) return <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>;
    if (isCountingDown) return `${cancelText} (${countdown}s)`;
    return children;
  };

  const getButtonVariant = () => {
    if (isCountingDown) return 'destructive';
    return variant;
  };

  return (
    <Button
      variant={getButtonVariant()}
      size={size}
      disabled={disabled || isLoading}
      className={className}
      onClick={handleClick}
    >
      {getButtonText()}
    </Button>
  );
};